import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { SecureForm } from '@/components/ui/secure-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Heart, Users, Award, Sparkles, CheckCircle2, Home } from 'lucide-react';
import CaregiverApplicationForm from '@/components/forms/CaregiverApplicationForm';

/* ================================
   Types
   ================================ */

export type CaregiverType = 'individual' | 'agency';
export type PlanKey = 'basic' | 'agency';

export interface CaregiverProfile {
  id: string;
  caregiver_type: CaregiverType;
  first_name?: string;
  last_name?: string;
  agency_name?: string;
  agency_license_number?: string;
  agency_website?: string;
  agency_phone?: string;
  agency_city?: string;
  agency_state?: string;
  agency_zip?: string;
  staff_count?: number;
  accepts_private_pay?: boolean;
  accepts_ltss_waiver?: boolean;
  accepts_medicare_advantage?: boolean;
  agency_insurance_verified?: boolean;
  premium_interest?: boolean;
  premium_contact_email?: string;
  verification_status?: 'none' | 'pending' | 'verified';
  created_at?: string;
  updated_at?: string;
}

/* ================================
   Invite Hero
   ================================ */

const InviteHero: React.FC<{ 
  onCaregiverClick?: () => void; 
  onAgencyClick?: () => void;
  activeMode?: 'caregiver' | 'agency';
}> = ({ onCaregiverClick, onAgencyClick, activeMode = 'caregiver' }) => {
  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-50 via-rose-50 to-pink-50 dark:from-orange-950/30 dark:via-rose-950/30 dark:to-pink-950/30 border border-orange-200/50 dark:border-orange-800/50">
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="relative px-6 py-8 md:px-10 md:py-10">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="w-6 h-6 text-rose-500" />
          <span className="text-sm font-medium text-rose-600 dark:text-rose-400">Join Our Care Family</span>
        </div>
        
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
          Welcome to the SeniorThrive Community! 💝
        </h1>

        <p className="mt-3 max-w-3xl text-muted-foreground text-lg">
          Connect with families who truly need your compassionate care. We're here to help you build meaningful 
          relationships and make caregiving more rewarding for everyone. 
        </p>

        <ul className="mt-6 grid gap-3 text-sm text-foreground md:grid-cols-2">
          <li className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            Get discovered by loving families near you
          </li>
          <li className="flex items-center gap-2">
            <Award className="w-4 h-4 text-blue-500" />
            Show off your amazing credentials with pride
          </li>
          <li className="flex items-center gap-2">
            <Users className="w-4 h-4 text-purple-500" />
            Receive warm introductions from families
          </li>
          <li className="flex items-center gap-2">
            <Home className="w-4 h-4 text-orange-500" />
            Simple tools that make your work easier
          </li>
          <li className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-pink-500" />
            Join a supportive community of caregivers
          </li>
          <li className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-rose-500" />
            Agencies can showcase their wonderful teams
          </li>
        </ul>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={onCaregiverClick}
            className={[
              "inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105",
              activeMode === 'caregiver' 
                ? "bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600"
                : "border-2 border-rose-300 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/50"
            ].join(' ')}
          >
            <Heart className="w-4 h-4" />
            I'm a Caregiver
          </button>

          <button
            type="button"
            onClick={onAgencyClick}
            className={[
              "inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105",
              activeMode === 'agency' 
                ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600"
                : "border-2 border-orange-300 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950/50"
            ].join(' ')}
          >
            <Users className="w-4 h-4" />
            We're an Agency
          </button>
        </div>

        <div className="mt-8 p-4 bg-white/60 dark:bg-black/20 rounded-2xl border border-white/50">
          <p className="text-sm text-muted-foreground text-center mb-3">
            "Families are actively searching for caring people like you right now" 💙
          </p>
          <div className="flex justify-center flex-wrap items-center gap-6 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              Background checks available
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              Local family connections
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-purple-500 animate-pulse" />
              Trusted by 1000+ families
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ================================
   Membership Step
   ================================ */

const MembershipCard: React.FC<{
  active: boolean;
  onClick: () => void;
  title: string;
  badge?: string;
  price?: string;
  bullets: string[];
}> = ({ active, onClick, title, badge, price, bullets }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'w-full text-left rounded-2xl border-2 p-6 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-br',
        active 
          ? 'border-primary ring-4 ring-primary/20 from-primary/5 to-primary/10 shadow-xl scale-[1.02]' 
          : 'border-border hover:border-primary/50 from-background to-muted/30',
      ].join(' ')}
      aria-pressed={active}
    >
      <div className="flex items-center gap-3">
        <div className={[
          'w-5 h-5 rounded-full border-2 flex items-center justify-center', 
          active ? 'border-primary bg-primary' : 'border-muted-foreground'
        ].join(' ')}>
          {active && <CheckCircle2 className="w-3 h-3 text-white" />}
        </div>
        <h3 className="font-bold text-foreground">{title}</h3>
        {badge && (
          <Badge 
            variant={active ? "default" : "secondary"} 
            className="text-xs font-medium"
          >
            {badge}
          </Badge>
        )}
        {price && <span className="ml-auto text-sm font-medium text-muted-foreground">{price}</span>}
      </div>
      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <Heart className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </button>
  );
};

const Step1Membership: React.FC<{ onNext: (choice: PlanKey) => void; preset?: PlanKey }> = ({ onNext, preset }) => {
  const [choice, setChoice] = useState<PlanKey>(preset ?? 'basic');

  useEffect(() => {
    if (preset) setChoice(preset);
  }, [preset]);

  return (
    <div id="membership" className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-bold text-foreground">Choose Your Journey With Us 🌟</h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Every family deserves amazing care, and every caregiver deserves to be celebrated. 
          Pick the path that feels right for your caring heart.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        <MembershipCard
          active={choice === 'basic'}
          onClick={() => setChoice('basic')}
          title="Individual Caregiver"
          badge="Free Forever"
          bullets={[
            'Beautiful profile in our directory', 
            'Connect with local families', 
            'Easy-to-use management tools', 
            'Join our caring community'
          ]}
        />
        <MembershipCard
          active={choice === 'agency'}
          onClick={() => setChoice('agency')}
          title="Care Team Agency"
          badge="New & Exciting"
          price="Free or Premium"
          bullets={[
            'Showcase your amazing team',
            'Display your service areas with pride',
            'Smart lead routing to your coordinators',
            'Premium placement options available',
            'Verified business credentials',
          ]}
        />
      </div>

      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-4">
          You're just one step away from connecting with families who need you! 💙
        </p>
        <Button 
          onClick={() => onNext(choice)} 
          className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 px-8 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transition-all duration-300"
        >
          Continue to Join Our Family <Heart className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

/* ================================
   Agency Form Step
   ================================ */

// Warm, encouraging field component
const FormField: React.FC<{
  id: string;
  label: string;
  value: string | number;
  type?: string;
  onChange: (v: string) => void;
  required?: boolean;
  helpText?: string;
}> = React.memo(({ id, label, value, type = 'text', onChange, required = false, helpText }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm font-medium text-foreground flex items-center gap-2">
      {label} 
      {required && <span className="text-rose-500">*</span>}
    </Label>
    <Input
      id={id}
      type={type}
      value={value as any}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full rounded-xl border-2 focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all duration-300"
    />
    {helpText && (
      <p className="text-xs text-muted-foreground">{helpText}</p>
    )}
  </div>
));

FormField.displayName = 'FormField';

const Step2AgencyForm: React.FC<{
  onContinue: (saved: CaregiverProfile) => void;
}> = ({ onContinue }) => {
  const [form, setForm] = useState({
    agency_name: '',
    agency_license_number: '',
    agency_website: '',
    agency_phone: '',
    agency_city: '',
    agency_state: '',
    agency_zip: '',
    staff_count: 1,
    accepts_private_pay: true,
    accepts_ltss_waiver: false,
    accepts_medicare_advantage: false,
    premium_interest: false,
    premium_contact_email: '',
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Memoized update function to prevent unnecessary re-renders
  const update = useCallback(<K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);

    try {
      const { data: result, error } = await supabase.functions.invoke('submit-professional-application', {
        body: {
          profession: 'Independent Caregiver',
          first_name: '',
          last_name: '',
          email: '',
          phone: form.agency_phone,
          caregiver_type: 'agency',
          agency_name: form.agency_name,
          agency_license_number: form.agency_license_number,
          agency_website: form.agency_website,
          agency_phone: form.agency_phone,
          agency_city: form.agency_city,
          agency_state: form.agency_state,
          agency_zip: form.agency_zip,
          staff_count: form.staff_count,
          accepts_private_pay: form.accepts_private_pay,
          accepts_ltss_waiver: form.accepts_ltss_waiver,
          accepts_medicare_advantage: form.accepts_medicare_advantage,
          agency_insurance_verified: false,
          premium_interest: form.premium_interest,
          premium_contact_email: form.premium_contact_email,
          terms_agreed: true,
        }
      });

      if (error) throw error;

      // Track Reddit Lead event for agency application
      if (typeof window !== 'undefined' && window.rdt) {
        window.rdt('track', 'Lead', {
          lead_type: 'caregiver_application',
          caregiver_type: 'agency',
          plan: form.premium_interest ? 'agency_premium' : 'agency_free',
          agency_name: form.agency_name,
          source: 'agency-application-form',
          page_path: window.location.pathname,
        });
      }

      // Remove toast, success will be shown in modal
      onContinue({
        id: result.id,
        caregiver_type: 'agency',
        ...form,
        verification_status: 'pending'
      });
    } catch (e: any) {
      console.error('Submission error:', e);
      setErr(e.message ?? 'Save failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8 space-y-3">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Users className="w-6 h-6 text-primary" />
          <span className="text-sm font-medium text-primary">Almost there!</span>
        </div>
        <h2 className="text-2xl font-bold text-foreground">Tell Us About Your Care Team 💙</h2>
        <p className="text-muted-foreground text-lg max-w-xl mx-auto">
          Help families get to know your amazing team! This is your chance to shine and show why you're special.
        </p>
      </div>

      {err && (
        <div className="mb-6 rounded-2xl bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 px-4 py-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-rose-500">⚠️</span>
            <span>Oops! {err}</span>
          </div>
        </div>
      )}

      <SecureForm onSubmit={handleSubmit} className="space-y-8">
        <Card className="border-2 border-orange-200/50 dark:border-orange-800/50 bg-gradient-to-br from-orange-50/50 to-amber-50/50 dark:from-orange-950/20 dark:to-amber-950/20 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Home className="w-5 h-5 text-orange-500" />
              Your Agency Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-6">
            <FormField 
              key="agency_name"
              id="agency-name"
              label="What's your agency called?" 
              value={form.agency_name} 
              onChange={(v) => update('agency_name', v)} 
              required 
              helpText="This is how families will find and remember you"
            />
            <FormField 
              key="agency_license_number"
              id="agency-license-number"
              label="Your credentials (we love that you're licensed!)" 
              value={form.agency_license_number} 
              onChange={(v) => update('agency_license_number', v)} 
              helpText="Show off those official credentials"
            />
            <FormField 
              key="agency_website"
              id="agency-website"
              label="Website (show off your online home!)" 
              value={form.agency_website} 
              onChange={(v) => update('agency_website', v)} 
              helpText="Let families learn more about you"
            />
            <FormField 
              key="agency_phone"
              id="agency-phone"
              label="Phone number for warm conversations" 
              value={form.agency_phone} 
              onChange={(v) => update('agency_phone', v)}
              required 
              helpText="How families can reach your caring team"
            />
            <FormField 
              key="agency_city"
              id="agency-city"
              label="What city do you call home?" 
              value={form.agency_city} 
              onChange={(v) => update('agency_city', v)}
              required 
            />
            <FormField 
              key="agency_state"
              id="agency-state"
              label="State" 
              value={form.agency_state} 
              onChange={(v) => update('agency_state', v)}
              required 
            />
            <FormField 
              key="agency_zip"
              id="agency-zip"
              label="ZIP code" 
              value={form.agency_zip} 
              onChange={(v) => update('agency_zip', v)}
              required 
            />
            <div key="staff_count" className="space-y-2">
              <Label htmlFor="staff-count" className="text-sm font-medium text-foreground flex items-center gap-2">
                How many amazing people are on your team? <span className="text-rose-500">*</span>
              </Label>
              <Input
                id="staff-count"
                type="number"
                min={1}
                value={form.staff_count}
                onChange={(e) => update('staff_count', Number(e.target.value))}
                required
                className="w-full rounded-xl border-2 focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all duration-300"
              />
              <p className="text-xs text-muted-foreground">Include everyone who provides direct care</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-200/50 dark:border-blue-800/50 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Award className="w-5 h-5 text-blue-500" />
              Payment Options You Accept
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Help families understand how they can work with you financially
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-1 gap-4">
              <div className="flex items-center space-x-3 p-3 rounded-xl bg-white/60 dark:bg-black/20">
                <Checkbox
                  id="private-pay"
                  checked={form.accepts_private_pay}
                  onCheckedChange={(checked) => update('accepts_private_pay', !!checked)}
                />
                <Label htmlFor="private-pay" className="text-sm text-foreground">
                  We accept private pay (families paying directly)
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-xl bg-white/60 dark:bg-black/20">
                <Checkbox
                  id="ltss-waiver"
                  checked={form.accepts_ltss_waiver}
                  onCheckedChange={(checked) => update('accepts_ltss_waiver', !!checked)}
                />
                <Label htmlFor="ltss-waiver" className="text-sm text-foreground">
                  We work with LTSS waiver programs
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-xl bg-white/60 dark:bg-black/20">
                <Checkbox
                  id="medicare-advantage"
                  checked={form.accepts_medicare_advantage}
                  onCheckedChange={(checked) => update('accepts_medicare_advantage', !!checked)}
                />
                <Label htmlFor="medicare-advantage" className="text-sm text-foreground">
                  We partner with Medicare Advantage plans
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Premium Interest Section */}
        <Card className="border-2 border-purple-200/50 dark:border-purple-800/50 bg-gradient-to-br from-purple-50/50 to-pink-50/50 dark:from-purple-950/20 dark:to-pink-950/20 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Exciting Premium Features Coming Soon! 
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Want to be first in line for amazing new features? We'd love to keep you in the loop!
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="premium-interest"
                checked={form.premium_interest}
                onCheckedChange={(checked) => update('premium_interest', !!checked)}
              />
              <Label htmlFor="premium-interest" className="text-sm font-medium text-foreground">
                🚀 Yes! Keep me updated on premium features
              </Label>
            </div>
            
            {form.premium_interest && (
              <div className="ml-6 space-y-4 p-4 bg-white/60 dark:bg-black/20 rounded-xl">
                <FormField 
                  key="premium_contact_email"
                  id="premium-contact-email"
                  label="Best email to reach you" 
                  value={form.premium_contact_email} 
                  onChange={(v) => update('premium_contact_email', v)}
                  type="email"
                  helpText="We'll only contact you about premium features, promise!"
                />
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="w-3 h-3 text-green-500" />
                <span>Priority placement in search results</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="w-3 h-3 text-blue-500" />
                <span>Advanced lead routing and analytics</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="w-3 h-3 text-purple-500" />
                <span>Enhanced profile features</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="w-3 h-3 text-rose-500" />
                <span>Dedicated account support</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center space-y-4">
          <p className="text-muted-foreground">
            You're about to join a community that celebrates amazing caregivers like you! 🎉
          </p>
          <Button 
            type="submit" 
            disabled={loading} 
            className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 px-8 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transition-all duration-300 min-w-48"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Adding you to our family...
              </>
            ) : (
              <>
                Join Our SeniorThrive Family <Heart className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </SecureForm>
    </div>
  );
};

/* ================================
   Success Celebration Modal
   ================================ */

const SuccessCelebrationModal: React.FC<{
  isOpen: boolean;
  profile: CaregiverProfile;
  onClose: () => void;
}> = ({ isOpen, profile, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto border-none bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-green-950/50 dark:via-emerald-950/50 dark:to-teal-950/50">
        <div className="text-center space-y-6 p-6">
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center animate-bounce">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-foreground">Welcome to Our Family! 🎉</h2>
              <p className="text-lg text-muted-foreground">
                We're absolutely thrilled to have {profile.agency_name} join the SeniorThrive community!
              </p>
            </div>
          </div>

          <div className="space-y-4 p-4 bg-white/60 dark:bg-black/20 rounded-2xl">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-yellow-500" />
              What happens next?
            </h3>
            <ul className="text-sm text-muted-foreground space-y-2 text-left">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                <span>Check your email for a warm welcome message</span>
              </li>
              <li className="flex items-start gap-2">
                <Users className="w-4 h-4 text-blue-500 mt-0.5" />
                <span>We'll review your information (usually within 24 hours)</span>
              </li>
              <li className="flex items-start gap-2">
                <Heart className="w-4 h-4 text-rose-500 mt-0.5" />
                <span>Start connecting with families who need your care</span>
              </li>
              <li className="flex items-start gap-2">
                <Award className="w-4 h-4 text-purple-500 mt-0.5" />
                <span>Get verified to earn your Trusted badge</span>
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 rounded-xl">
              <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                💙 "Every family deserves amazing care, and you're part of making that happen"
              </p>
            </div>
            
            <Button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-full font-medium py-3 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start My Journey <Heart className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ================================
   Wrapper
   ================================ */

export default function CaregiverAgencyOnboarding(props: {
  planPreset?: PlanKey;
  onComplete?: (profile: CaregiverProfile | null) => void;
}) {
  // Skip membership selection if planPreset is provided
  const initialStep = props.planPreset === 'agency' ? 'agency' : 'select';
  const [step, setStep] = useState<'select' | 'agency' | 'individual'>(initialStep);
  const [preset, setPreset] = useState<PlanKey | undefined>(props.planPreset);
  const [savedProfile, setSavedProfile] = useState<CaregiverProfile | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Read plan from URL if present
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const sp = new URLSearchParams(window.location.search);
      const plan = sp.get('plan') as PlanKey | null;
      if (plan === 'agency') {
        setPreset('agency');
        setStep('agency');
      }
    }
  }, []);

  const header = useMemo(() => {
    if (step === 'select') return 'Choose Your Membership Level';
    if (step === 'agency') return 'Join as an Agency';
    if (step === 'individual') return 'Individual Caregiver Application';
    return '';
  }, [step]);

  const activeMode = useMemo(() => {
    if (step === 'agency') return 'agency';
    if (step === 'select' && preset === 'agency') return 'agency';
    return 'caregiver';
  }, [step, preset]);

  function handleNext(choice: PlanKey) {
    if (choice === 'agency') {
      setStep('agency');
    } else {
      // Show individual caregiver form for 'basic' and 'trustedPro'
      setStep('individual');
    }
  }

  function handleCaregiverClick() {
    setStep('select');
    setPreset('basic');
  }

  function handleAgencyClick() {
    setStep('agency');
    setPreset('agency');
  }

  const handleAgencySubmit = useCallback((profile: CaregiverProfile) => {
    setSavedProfile(profile);
    setShowSuccess(true);
    props.onComplete?.(profile);
  }, [props]);

  const handleSuccessClose = useCallback(() => {
    setShowSuccess(false);
  }, []);

  // Show success modal if profile saved
  if (savedProfile && showSuccess) {
    return (
      <SuccessCelebrationModal 
        isOpen={showSuccess}
        profile={savedProfile}
        onClose={handleSuccessClose}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
      {/* Only show hero section if not directly entering agency flow */}
      {!props.planPreset && (
        <InviteHero 
          onCaregiverClick={handleCaregiverClick}
          onAgencyClick={handleAgencyClick}
          activeMode={activeMode}
        />
      )}

      <div className="mb-6 mt-8">
        <h1 className="text-2xl font-semibold text-foreground">{header}</h1>
        <p className="text-muted-foreground">
          {step === 'select' ? 'Select your verification path' : 'Complete your agency registration'}
        </p>
      </div>

      {step === 'select' && <Step1Membership onNext={handleNext} preset={preset} />}

      {step === 'agency' && (
        <div className="space-y-6">
          {/* Show different back navigation based on how user got here */}
          <Button 
            variant="ghost" 
            onClick={() => {
              if (props.planPreset === 'agency') {
                // Go back to agency options page
                window.history.back();
              } else {
                setStep('select');
              }
            }}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back to {props.planPreset === 'agency' ? 'agency options' : 'membership selection'}
          </Button>
          <Step2AgencyForm
            onContinue={handleAgencySubmit}
          />
        </div>
      )}

      {step === 'individual' && (
        <div className="space-y-6">
          <Button 
            variant="ghost" 
            onClick={() => setStep('select')} 
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back to membership selection
          </Button>
          <CaregiverApplicationForm />
        </div>
      )}

      {/* Simplified success state - modal handles the celebration */}
      {savedProfile && !showSuccess && (
        <div className="fixed bottom-4 right-4 p-4 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-lg shadow-lg">
          <p className="text-sm">Welcome to SeniorThrive! 🎉</p>
        </div>
      )}
    </div>
  );
}